/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { FaShieldAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";

// Redux
// eslint-disable-next-line no-unused-vars
import { useSelector, useDispatch } from "react-redux";
import {
  useCreateNewOrderMutation,
  useEstimateShippingCostMutation,
} from "../../redux/api/orderApi";
import { clearCart } from "../../redux/features/cartSlice";

import { caluclateOrderCost } from "../../helpers/caluclateOrderCost";

import MetaData from "../../components/layout/MetaData";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";
import { getCartItemLink, getCartItemKey } from "../../helpers/cartItem";
import "../Payment/CheckoutPage.css";

const DEFAULT_ITEM_WEIGHT_GRAMS = 500;

const COUNTRY_CODE_MAP = {
  australia: "AU",
  "united states": "US",
  usa: "US",
  canada: "CA",
  china: "CN",
  germany: "DE",
  france: "FR",
  japan: "JP",
  singapore: "SG",
  "united kingdom": "GB",
  uk: "GB",
};

/**
 * 输出结算页运费诊断日志，便于定位 4PX 预估和兜底运费的切换原因。
 * @param {string} message 日志说明
 * @param {Object} details 诊断详情
 */
const logShippingEstimateDebug = (message, details = {}) => {
  console.log(`[ConfirmOrder][ShippingEstimate] ${message}`, details);
};

/**
 * 将国家信息标准化为 ISO 两位国家码。
 * @param {string} country 收货国家（名称或国家码）
 * @returns {string} ISO 两位国家码，无法识别时返回空字符串
 */
const normalizeCountryCode = (country) => {
  const rawValue = String(country || "").trim();
  if (!rawValue) {
    return "";
  }

  if (/^[A-Za-z]{2}$/.test(rawValue)) {
    return rawValue.toUpperCase();
  }

  const mappedCode = COUNTRY_CODE_MAP[rawValue.toLowerCase()];
  return mappedCode || "";
};

/**
 * 解析商品重量并转换为克（g）。
 * @param {number|string} weight 商品重量
 * @returns {number|null} 克数，无法解析时返回 null
 */
const parseWeightToGrams = (weight) => {
  if (weight === null || weight === undefined) {
    return null;
  }

  const weightText = String(weight).trim().toLowerCase();
  if (!weightText) {
    return null;
  }

  const parsedWeight = Number.parseFloat(weightText.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(parsedWeight) || parsedWeight <= 0) {
    return null;
  }

  if (weightText.includes("kg")) {
    return parsedWeight * 1000;
  }

  return parsedWeight;
};

/**
 * 计算购物车总重量（克）。
 * @param {Array} items 购物车商品列表
 * @returns {number} 总重量（克）
 */
const getCartTotalWeightInGrams = (items = []) => {
  return items.reduce((totalWeight, item) => {
    const itemWeight = parseWeightToGrams(item?.weight ?? item?.Weight);
    // TODO: 当前购物车未统一存储重量字段，暂用 500g 作为兜底值，后续可改为真实商品重量。
    const normalizedWeight = Number.isFinite(itemWeight)
      ? itemWeight
      : DEFAULT_ITEM_WEIGHT_GRAMS;
    const quantity = Number(item?.quantity || 0);
    return totalWeight + normalizedWeight * quantity;
  }, 0);
};

/**
 * 构建购物车运费预估诊断快照。
 * @param {Array} items 购物车商品列表
 * @returns {Array} 商品数量、重量和金额摘要
 */
const buildCartShippingDebugItems = (items = []) => {
  return items.map((item) => {
    const rawWeight = item?.weight ?? item?.Weight ?? null;
    const parsedWeight = parseWeightToGrams(rawWeight);
    const quantity = Number(item?.quantity || 0);
    const unitWeight = Number.isFinite(parsedWeight)
      ? parsedWeight
      : DEFAULT_ITEM_WEIGHT_GRAMS;

    return {
      name: item?.name,
      quantity,
      price: Number(item?.price || 0),
      rawWeight,
      parsedWeight,
      usedDefaultWeight: !Number.isFinite(parsedWeight),
      unitWeightInGrams: unitWeight,
      lineWeightInGrams: unitWeight * quantity,
      lineAmount: Number((Number(item?.price || 0) * quantity).toFixed(2)),
    };
  });
};

/**
 * 从后端预估响应中提取可用运费。
 * @param {Object} response 后端 `/logistics/estimate` 响应
 * @returns {number|null} 运费金额，无法提取时返回 null
 */
const extractEstimatedShippingFee = (response) => {
  const estimateWrapper = response?.estimate || {};
  const quoteList = Array.isArray(estimateWrapper?.quotes)
    ? estimateWrapper.quotes
    : [];
  const rawCostList = Array.isArray(estimateWrapper?.raw?.costList)
    ? estimateWrapper.raw.costList
    : [];

  const candidates = [
    estimateWrapper?.estimate,
    quoteList[0],
    rawCostList[0],
  ].filter(Boolean);

  logShippingEstimateDebug("开始解析 4PX 运费响应", {
    quoteCount: quoteList.length,
    rawCostCount: rawCostList.length,
    candidateCount: candidates.length,
    firstEstimate: estimateWrapper?.estimate || null,
    rawResponse: response,
  });

  for (const candidate of candidates) {
    const rawFee =
      candidate?.lumpSumFee ??
      candidate?.lump_sum_fee ??
      candidate?.totalFee ??
      candidate?.total_fee ??
      candidate?.estimatedCost ??
      candidate?.estimated_cost ??
      candidate?.fee ??
      candidate?.cost;
    const parsedFee = Number(rawFee);
    logShippingEstimateDebug("检查 4PX 运费候选字段", {
      candidate,
      rawFee,
      parsedFee,
    });
    if (Number.isFinite(parsedFee) && parsedFee >= 0) {
      return Number(parsedFee.toFixed(2));
    }
  }

  return null;
};

/**
 * 确认订单页：展示订单信息并提交创建订单。
 */
const ConfirmOrder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const { cartItems, shippingInfo, paymentMethod } = useSelector(
    (state) => state.cart
  );
  const { user } = useSelector((state) => state.auth);
  const {
    itemsPrice: fallbackItemsPrice,
    shippingPrice: fallbackShippingPrice,
    taxPrice: fallbackTaxPrice,
  } = caluclateOrderCost(cartItems);
  const [estimatedShippingPrice, setEstimatedShippingPrice] = useState(null);
  const [shippingPriceSource, setShippingPriceSource] = useState("fallback");

  const itemsPrice = Number(fallbackItemsPrice).toFixed(2);
  const shippingPrice =
    estimatedShippingPrice === null ? fallbackShippingPrice : estimatedShippingPrice;
  const totalPrice = (
    Number(itemsPrice) +
    Number(shippingPrice) +
    Number(fallbackTaxPrice)
  ).toFixed(2);
  const itemCount = cartItems.reduce(
    (total, item) => total + Number(item?.quantity || 0),
    0
  );

  const [createNewOrder, { isLoading, error, isSuccess }] =
    useCreateNewOrderMutation();
  const [estimateShippingCost] = useEstimateShippingCostMutation();

  /**
   * 格式化单价显示为 6 位小数，避免因后端返回类型不一致导致渲染异常。
   * @param {number|string} price 商品单价
   * @returns {string} 固定 6 位小数字符串
   */
  const formatUnitPrice = (price) => Number(price || 0).toFixed(6);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Order Placed Successfully");
    }
    if (error) {
      toast.error(error?.data?.message);
    }
  }, [isSuccess, error]);

  useEffect(() => {
    /**
     * 根据购物车与收货国家调用 4PX 运费预估。
     */
    const estimateOrderShipping = async () => {
      if (!cartItems?.length) {
        logShippingEstimateDebug("回退到固定运费：购物车为空", {
          fallbackShippingPrice,
        });
        setEstimatedShippingPrice(null);
        setShippingPriceSource("fallback");
        return;
      }

      const countryCode = normalizeCountryCode(shippingInfo?.country);
      if (!countryCode) {
        logShippingEstimateDebug("回退到固定运费：收货国家无法识别为国家码", {
          rawCountry: shippingInfo?.country,
          fallbackShippingPrice,
        });
        setEstimatedShippingPrice(null);
        setShippingPriceSource("fallback");
        return;
      }

      const totalWeightInGrams = Math.max(
        1,
        Math.round(getCartTotalWeightInGrams(cartItems))
      );

      const estimatePayload = {
        country_code: countryCode,
        weight: String(totalWeightInGrams),
        cargocode: "P",
      };

      logShippingEstimateDebug("准备请求 4PX 运费预估", {
        payload: estimatePayload,
        rawCountry: shippingInfo?.country,
        fallbackShippingPrice,
        itemsPrice,
        cartItems: buildCartShippingDebugItems(cartItems),
      });

      try {
        const estimateResponse = await estimateShippingCost(estimatePayload).unwrap();

        logShippingEstimateDebug("收到后端 4PX 运费预估响应", {
          response: estimateResponse,
        });

        const estimatedFee = extractEstimatedShippingFee(estimateResponse);
        if (Number.isFinite(estimatedFee)) {
          logShippingEstimateDebug("使用 4PX 运费预估金额", {
            estimatedFee,
            fallbackShippingPrice,
          });
          setEstimatedShippingPrice(estimatedFee);
          setShippingPriceSource("4px");
          return;
        }

        logShippingEstimateDebug("回退到固定运费：4PX 响应中未解析到可用费用字段", {
          fallbackShippingPrice,
          response: estimateResponse,
        });
      } catch (estimateError) {
        console.error("[ConfirmOrder] 4PX 运费预估失败，回退到固定运费:", {
          error: estimateError,
          fallbackShippingPrice,
        });
      }

      logShippingEstimateDebug("使用固定兜底运费", {
        fallbackShippingPrice,
      });
      setEstimatedShippingPrice(null);
      setShippingPriceSource("fallback");
    };

    estimateOrderShipping();
  }, [cartItems, shippingInfo?.country, estimateShippingCost]);

  /**
   * 提交订单：创建成功后清空购物车并跳转订单详情页。
   */
  const submitHandler = async () => {
    const {
      itemsPrice: recalculatedItemsPrice,
      shippingPrice: recalculatedShippingPrice,
      taxPrice,
    } = caluclateOrderCost(cartItems);
    const finalShippingPrice =
      estimatedShippingPrice === null
        ? recalculatedShippingPrice
        : estimatedShippingPrice;
    const finalTotalPrice = (
      Number(recalculatedItemsPrice) +
      Number(finalShippingPrice) +
      Number(taxPrice)
    ).toFixed(2);

    // Create WireTransfer Order
    const orderData = {
      orderItems: cartItems,
      shippingInfo,
      paymentMethod: paymentMethod,
      paymentInfo: {
        status: "Not Paid",
      },
      itemsPrice: Number(recalculatedItemsPrice),
      shippingAmount: Number(finalShippingPrice),
      taxAmount: taxPrice,
      totalAmount: Number(finalTotalPrice),
    };
    const res = await createNewOrder(orderData);
    if (res) {
      dispatch(clearCart());
      navigate(`/me/order/${res?.data?.order?._id}`);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <MetaData title="Confirm Order" />
      <main className="checkout-reference-page">
        <div className="checkout-reference-container">
          <nav className="checkout-breadcrumb" aria-label="Breadcrumb">
            <Link to="/cart"><i className="fas fa-shopping-cart" aria-hidden="true"></i>Shopping Cart</Link>
            <span>/</span>
            <strong>Confirm Order</strong>
          </nav>

          <div className="checkout-review-bars" aria-label="Checkout progress">
            <div><span>Shipping Address</span><Link to="/shipping">Edit</Link></div>
            <div><span>Payment Method</span><Link to="/payment_method">Edit</Link></div>
          </div>

          <div className="checkout-reference-layout">
            <section className="checkout-main-column">
              <h1>Confirm and Pay</h1>
              <div className="checkout-panel confirm-order-panel">
                <div className="confirm-info-block">
                  <h2>Shipping</h2>
                  <p><strong>Name:</strong> {user?.name}</p>
                  <p><strong>Phone:</strong> {shippingInfo?.phoneNo}</p>
                  <p><strong>Address:</strong> {`${shippingInfo?.address}, ${shippingInfo?.city}, ${shippingInfo?.zipCode}, ${shippingInfo?.country}`}</p>
                </div>

                <div className="confirm-info-block">
                  <h2>Payment Method</h2>
                  <p><strong>Method:</strong> {paymentMethod}</p>
                </div>

                <div className="confirm-info-block">
                  <h2>Order Items</h2>
                  {cartItems.length === 0 ? (
                    <Message>Your cart is empty</Message>
                  ) : (
                    <table className="confirm-items-table">
                      <thead><tr><th>Product</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
                      <tbody>
                        {cartItems.map((item) => (
                          <tr key={getCartItemKey(item)}>
                            <td>
                              <div className="confirm-product-cell">
                                <img src={Array.isArray(item?.image) ? item?.image[0] : item?.image} alt="Product" />
                                {getCartItemLink(item) ? (
                                  <Link to={getCartItemLink(item)}>{item?.name}</Link>
                                ) : (
                                  <div>
                                    <span>{item?.name}</span>
                                    {item?.quoteSnapshot?.sizeLabel ? (
                                      <small>{item?.quoteSnapshot?.sizeLabel} / {item?.quoteSnapshot?.layers} Layers / Qty {item?.quoteSnapshot?.pcbQuantity}</small>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>{item?.quantity}</td>
                            <td>${formatUnitPrice(item?.price)}</td>
                            <td><strong>${(item?.quantity * item.price).toFixed(2)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </section>

            <aside className="checkout-sidebar">
              <form onSubmit={submitHandler} className="checkout-summary-card">
                <h2>Summary</h2>
                <dl>
                  <div><dt>Items in cart:</dt><dd>{itemCount}</dd></div>
                  <div><dt>Subtotal:</dt><dd>${itemsPrice}</dd></div>
                  <div><dt>Shipping:</dt><dd>${Number(shippingPrice).toFixed(2)}</dd></div>
                  {shippingPriceSource === "4px" && <div><dt>Shipping source:</dt><dd>Estimated by 4PX</dd></div>}
                  <div className="checkout-summary-total"><dt>Total:</dt><dd>${totalPrice}</dd></div>
                </dl>
                <div className="confirm-sidebar-actions">
                  <button className="checkout-primary-button" aria-label="Checkout via PayPal" type="submit">Checkout via PayPal</button>
                  <button type="button" onClick={() => setShowModal(true)} className="checkout-secondary-button" aria-label="Checkout via Wire Transfer">Checkout via Wire Transfer</button>
                </div>
              </form>
              <div className="checkout-security-card">
                <FaShieldAlt aria-hidden="true" />
                <span>Your data will be encrypted and transmitted securely.</span>
              </div>
            </aside>
          </div>
        </div>

        {showModal && (
          <div className="checkout-modal-backdrop" id="my-modal" role="dialog" aria-modal="true" aria-labelledby="wire-transfer-title">
            <div className="checkout-modal">
              <h3 id="wire-transfer-title">Bank Transfer Instructions</h3>
              <p>You are one step away from completing your order. To complete the order process, transfer the cart amount to the following bank account. Total cart value: <strong>${totalPrice}</strong>.</p>
              <p>
                <strong>Beneficiary:</strong> Creatyea Technology (SZ) CO., Limited<br />
                <strong>Bank:</strong> CHINA EVERBRIGHT BANK SHENZHEN BRANCH<br />
                <strong>A/C code:</strong> 38911488000024600<br />
                <strong>Bank Address:</strong> CEB BUILDING NO.18 ZIZHU QI AVENUE, ZHUZILIN SI ROAD, FUTIAN DISTRICT, SHENZHEN, CHINA 518040<br />
                <strong>SWIFT:</strong> EVERCNBJSZ1
              </p>
              <p>Once the payment is made, send the payment confirmation details like date, reference number, screenshot etc to: <a href="mailto:hello@creatyea.com">hello@creatyea.com</a></p>
              <div className="checkout-modal-actions">
                <button type="button" onClick={submitHandler} className="checkout-primary-button" disabled={isLoading}>
                  {isLoading ? <i className="fas fa-spinner fa-pulse"></i> : "Confirm Order"}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="checkout-modal-close">Close</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default ConfirmOrder;
